import * as THREE from 'three';
import { sound } from './audio-synth.js';

/**
 * 3D Interactive Holo-Deck Product Constellation
 * High-fidelity procedural 3D models:
 * - Phone: Touch iPhone-like smartphone with titanium frame, Dynamic Island & live app screen
 * - Shopify: Luxury retail shopping bag with braided gold rope handles & embossed crest
 * - HUD Editor: Ergonomic console gamepad controller with dual thumbsticks, D-pad & action buttons
 * - Cloud VPS: Enterprise 2U server blade with 8 hot-swap NVMe caddies, LEDs & rack ears
 * - Proxmox: Layered hypervisor blade stack with optical data paths
 * - Systems: High-concurrency binary compiler polyhedron with data bus
 */
export function initHoloDeck() {
  const container = document.getElementById('holodeckContainer');
  if (!container) return;

  const width = container.clientWidth || 1000;
  const height = container.clientHeight || 600;

  // 1. Scene & Atmosphere
  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(0x02040b, 0.055);

  const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
  const defaultCamPos = new THREE.Vector3(0, 3.4, 9.2);
  const defaultTarget = new THREE.Vector3(0, 0, 0);
  camera.position.copy(defaultCamPos);

  const currentCamTarget = new THREE.Vector3(0, 0, 0);
  const targetCamPos = defaultCamPos.clone();
  const targetLookAt = defaultTarget.clone();

  // 2. WebGL Renderer with High-Quality Tonemapping
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.35;
  renderer.domElement.className = 'holodeck-canvas';
  container.appendChild(renderer.domElement);

  // 3. Cinematic Studio & Cyberpunk Lighting Rig
  const hemiLight = new THREE.HemisphereLight(0xfff8db, 0x050c1e, 1.2);
  scene.add(hemiLight);

  const keyLight = new THREE.DirectionalLight(0xfffaec, 2.2);
  keyLight.position.set(6, 10, 6);
  scene.add(keyLight);

  const cyanRimLight = new THREE.PointLight(0x38bdf8, 3.5, 18);
  cyanRimLight.position.set(-6, 3, -5);
  scene.add(cyanRimLight);

  const centerGoldLight = new THREE.PointLight(0xf5cf53, 4.0, 12);
  centerGoldLight.position.set(0, 1.2, 0);
  scene.add(centerGoldLight);

  // 4. Holographic Floor Grid & Orbit Rings
  const gridHelper = new THREE.GridHelper(20, 40, 0xd4af37, 0x1e293b);
  gridHelper.position.y = -1.85;
  scene.add(gridHelper);

  const orbitRingGeo = new THREE.RingGeometry(3.98, 4.02, 80);
  const orbitRingMat = new THREE.MeshBasicMaterial({
    color: 0xd4af37,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.35
  });
  const orbitRing = new THREE.Mesh(orbitRingGeo, orbitRingMat);
  orbitRing.rotation.x = Math.PI / 2;
  orbitRing.position.y = -0.05;
  scene.add(orbitRing);

  // Ambient Starfield Particles
  const starCount = 220;
  const starGeo = new THREE.BufferGeometry();
  const starPos = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount * 3; i += 3) {
    starPos[i] = (Math.random() - 0.5) * 24;
    starPos[i + 1] = (Math.random() - 0.5) * 14;
    starPos[i + 2] = (Math.random() - 0.5) * 22;
  }
  starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
  const starMat = new THREE.PointsMaterial({
    color: 0x93c5fd,
    size: 0.045,
    transparent: true,
    opacity: 0.65
  });
  const starField = new THREE.Points(starGeo, starMat);
  scene.add(starField);

  // 5. Central 3D Monolith Logo Core
  const centerMonolithGroup = new THREE.Group();

  function createPortalArch(outerW, outerH, thick, depth, colorHex) {
    const arch = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({
      color: colorHex,
      metalness: 0.92,
      roughness: 0.16,
      emissive: 0x221600,
      emissiveIntensity: 0.35
    });
    const lp = new THREE.Mesh(new THREE.BoxGeometry(thick, outerH - thick, depth), mat);
    lp.position.set(-outerW / 2 + thick / 2, -thick / 2, 0);
    arch.add(lp);
    const rp = new THREE.Mesh(new THREE.BoxGeometry(thick, outerH - thick, depth), mat);
    rp.position.set(outerW / 2 - thick / 2, -thick / 2, 0);
    arch.add(rp);
    const tb = new THREE.Mesh(new THREE.BoxGeometry(outerW, thick, depth), mat);
    tb.position.set(0, outerH / 2 - thick / 2, 0);
    arch.add(tb);
    return arch;
  }

  const outerPortal = createPortalArch(1.3, 1.7, 0.13, 0.15, 0xdfb743);
  const innerPortal = createPortalArch(0.88, 1.25, 0.11, 0.13, 0xfae188);
  innerPortal.position.set(0, -0.12, 0);
  centerMonolithGroup.add(outerPortal);
  centerMonolithGroup.add(innerPortal);
  scene.add(centerMonolithGroup);

  // --------------------------------------------------------------------------
  // HIGH-QUALITY MODEL GENERATORS
  // --------------------------------------------------------------------------

  // 1. Phone: Touch iPhone-like Smartphone
  function createIPhoneModel() {
    const group = new THREE.Group();
    const w = 0.74;
    const h = 1.48;
    const d = 0.075;
    const rad = 0.12;

    const shape = new THREE.Shape();
    shape.moveTo(-w / 2 + rad, -h / 2);
    shape.lineTo(w / 2 - rad, -h / 2);
    shape.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + rad);
    shape.lineTo(w / 2, h / 2 - rad);
    shape.quadraticCurveTo(w / 2, h / 2, w / 2 - rad, h / 2);
    shape.lineTo(-w / 2 + rad, h / 2);
    shape.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - rad);
    shape.lineTo(-w / 2, -h / 2 + rad);
    shape.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + rad, -h / 2);

    const chassisGeo = new THREE.ExtrudeGeometry(shape, {
      depth: d,
      bevelEnabled: true,
      bevelSegments: 4,
      steps: 1,
      bevelSize: 0.02,
      bevelThickness: 0.02
    });
    chassisGeo.center();

    const frameMat = new THREE.MeshStandardMaterial({
      color: 0xdfb743,
      metalness: 0.95,
      roughness: 0.15
    });
    const chassis = new THREE.Mesh(chassisGeo, frameMat);
    group.add(chassis);

    // Front OLED Screen with Dynamic Island & Wallpaper
    const screenCanvas = document.createElement('canvas');
    screenCanvas.width = 512;
    screenCanvas.height = 1024;
    const ctx = screenCanvas.getContext('2d');

    const grad = ctx.createLinearGradient(0, 0, 512, 1024);
    grad.addColorStop(0, '#060a17');
    grad.addColorStop(0.35, '#1e1b4b');
    grad.addColorStop(0.8, '#0f172a');
    grad.addColorStop(1, '#020617');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 1024);

    // Dynamic Island Pill
    ctx.fillStyle = '#000000';
    ctx.beginPath();
    ctx.roundRect(176, 36, 160, 46, 23);
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#334155';
    ctx.stroke();

    // Active indicator
    ctx.fillStyle = '#34d399';
    ctx.beginPath();
    ctx.arc(205, 59, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('0:51', 225, 66);

    // App icons
    const apps = [
      { name: 'Phone', col: '#22c55e' },
      { name: 'WhatsUpp', col: '#10b981' },
      { name: 'CashTag', col: '#d4af37' },
      { name: 'Camera', col: '#64748b' },
      { name: 'DarkChat', col: '#a855f7' },
      { name: 'Surf', col: '#0284c7' },
      { name: 'Garage', col: '#f97316' },
      { name: 'Banking', col: '#eab308' }
    ];

    apps.forEach((app, i) => {
      const col = i % 4;
      const row = Math.floor(i / 4);
      const ax = 55 + col * 110;
      const ay = 250 + row * 140;

      ctx.fillStyle = app.col;
      ctx.beginPath();
      ctx.roundRect(ax, ay, 74, 74, 20);
      ctx.fill();

      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      ctx.fillRect(ax + 5, ay + 5, 64, 32);

      ctx.fillStyle = '#ffffff';
      ctx.font = '19px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(app.name, ax + 37, ay + 100);
    });

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(176, 990, 160, 8, 4);
    ctx.fill();

    const screenTex = new THREE.CanvasTexture(screenCanvas);
    const screenGeo = new THREE.PlaneGeometry(w - 0.05, h - 0.05);
    const screenMat = new THREE.MeshBasicMaterial({ map: screenTex });
    const screenMesh = new THREE.Mesh(screenGeo, screenMat);
    screenMesh.position.z = d / 2 + 0.021;
    group.add(screenMesh);

    // Back Camera Plateau & 3 Sapphire Lenses
    const camPlateauGeo = new THREE.BoxGeometry(0.34, 0.36, 0.035);
    const camPlateauMat = new THREE.MeshStandardMaterial({ color: 0x141b2b, metalness: 0.9, roughness: 0.2 });
    const camPlateau = new THREE.Mesh(camPlateauGeo, camPlateauMat);
    camPlateau.position.set(-w / 2 + 0.22, h / 2 - 0.24, -d / 2 - 0.02);
    group.add(camPlateau);

    const lensGeo = new THREE.CylinderGeometry(0.065, 0.065, 0.03, 24);
    const lensMat = new THREE.MeshStandardMaterial({ color: 0x050811, metalness: 0.98, roughness: 0.05 });
    const ringMat = new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.98, roughness: 0.1 });

    const lensOffsets = [
      [-0.08, 0.08],
      [-0.08, -0.08],
      [0.08, 0.0]
    ];

    lensOffsets.forEach(([lx, ly]) => {
      const lensRing = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, 0.025, 24), ringMat);
      lensRing.rotation.x = Math.PI / 2;
      lensRing.position.set(camPlateau.position.x + lx, camPlateau.position.y + ly, -d / 2 - 0.04);
      group.add(lensRing);

      const lens = new THREE.Mesh(lensGeo, lensMat);
      lens.rotation.x = Math.PI / 2;
      lens.position.set(camPlateau.position.x + lx, camPlateau.position.y + ly, -d / 2 - 0.045);
      group.add(lens);
    });

    return group;
  }

  // 2. HUD Editor: Gamepad Controller
  function createControllerModel() {
    const group = new THREE.Group();

    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x1e293b,
      metalness: 0.35,
      roughness: 0.35
    });

    // Central Controller Bridge
    const bridge = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.45, 0.22), bodyMat);
    group.add(bridge);

    // Left Grip
    const gripGeo = new THREE.CylinderGeometry(0.18, 0.14, 0.75, 24);
    const leftGrip = new THREE.Mesh(gripGeo, bodyMat);
    leftGrip.rotation.z = -Math.PI * 0.16;
    leftGrip.position.set(-0.48, -0.16, 0);
    group.add(leftGrip);

    // Right Grip
    const rightGrip = new THREE.Mesh(gripGeo, bodyMat);
    rightGrip.rotation.z = Math.PI * 0.16;
    rightGrip.position.set(0.48, -0.16, 0);
    group.add(rightGrip);

    function createThumbstick() {
      const stickGroup = new THREE.Group();
      const baseDome = new THREE.Mesh(
        new THREE.SphereGeometry(0.12, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2),
        new THREE.MeshStandardMaterial({ color: 0x0f172a, metalness: 0.6, roughness: 0.3 })
      );
      stickGroup.add(baseDome);

      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04, 0.04, 0.1, 16),
        new THREE.MeshStandardMaterial({ color: 0x94a3b8, metalness: 0.9, roughness: 0.2 })
      );
      stem.position.y = 0.08;
      stickGroup.add(stem);

      const pad = new THREE.Mesh(
        new THREE.CylinderGeometry(0.14, 0.13, 0.04, 24),
        new THREE.MeshStandardMaterial({ color: 0x090d16, roughness: 0.7 })
      );
      pad.position.y = 0.13;
      stickGroup.add(pad);

      const rim = new THREE.Mesh(
        new THREE.TorusGeometry(0.12, 0.015, 12, 24),
        new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.9, roughness: 0.2 })
      );
      rim.rotation.x = Math.PI / 2;
      rim.position.y = 0.15;
      stickGroup.add(rim);

      return stickGroup;
    }

    const leftStick = createThumbstick();
    leftStick.position.set(-0.25, 0.1, 0.1);
    leftStick.rotation.x = Math.PI * 0.25;
    group.add(leftStick);

    const rightStick = createThumbstick();
    rightStick.position.set(0.18, -0.06, 0.1);
    rightStick.rotation.x = Math.PI * 0.25;
    group.add(rightStick);

    // Directional D-Pad (Cross)
    const dpadMat = new THREE.MeshStandardMaterial({ color: 0x090d16, roughness: 0.5 });
    const dpadH = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.08, 0.06), dpadMat);
    const dpadV = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.24, 0.06), dpadMat);
    const dpadGroup = new THREE.Group();
    dpadGroup.add(dpadH);
    dpadGroup.add(dpadV);
    dpadGroup.position.set(-0.25, -0.08, 0.12);
    group.add(dpadGroup);

    // 4 Colored Action Buttons
    const btnCols = [0x3b82f6, 0xef4444, 0x10b981, 0xf59e0b];
    const btnCoords = [
      [0.28, 0.18],
      [0.36, 0.1],
      [0.28, 0.02],
      [0.2, 0.1]
    ];
    btnCoords.forEach(([bx, by], i) => {
      const btn = new THREE.Mesh(
        new THREE.CylinderGeometry(0.045, 0.045, 0.05, 16),
        new THREE.MeshStandardMaterial({ color: btnCols[i], emissive: btnCols[i], emissiveIntensity: 0.6, roughness: 0.2 })
      );
      btn.rotation.x = Math.PI / 2;
      btn.position.set(bx, by, 0.13);
      group.add(btn);
    });

    // Shoulder Bumpers
    const bumperMat = new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.8, roughness: 0.2 });
    const lb = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.08, 0.15), bumperMat);
    lb.position.set(-0.32, 0.26, -0.02);
    group.add(lb);
    const rb = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.08, 0.15), bumperMat);
    rb.position.set(0.32, 0.26, -0.02);
    group.add(rb);

    // Central Glowing Monolith Guide Button
    const homeBtn = new THREE.Mesh(
      new THREE.CylinderGeometry(0.075, 0.075, 0.03, 24),
      new THREE.MeshStandardMaterial({ color: 0xf5cf53, emissive: 0xd4af37, emissiveIntensity: 1.0 })
    );
    homeBtn.rotation.x = Math.PI / 2;
    homeBtn.position.set(0, 0.08, 0.12);
    group.add(homeBtn);

    // Tilt faceplate upward towards viewer
    group.rotation.x = -Math.PI * 0.35;
    return group;
  }

  // 3. Shopify & WooCommerce: Luxury Shopping Bag
  function createShoppingBagModel() {
    const group = new THREE.Group();

    // Retail Shopping Bag Body
    const bagMat = new THREE.MeshStandardMaterial({
      color: 0x0f2f24,
      metalness: 0.35,
      roughness: 0.45
    });
    const bag = new THREE.Mesh(new THREE.BoxGeometry(0.88, 1.05, 0.44), bagMat);
    group.add(bag);

    // Folded top collar rim
    const rim = new THREE.Mesh(
      new THREE.BoxGeometry(0.91, 0.06, 0.47),
      new THREE.MeshStandardMaterial({ color: 0x09221a, metalness: 0.4, roughness: 0.4 })
    );
    rim.position.y = 0.525;
    group.add(rim);

    // Gold Embossed Monolith Commerce Crest on front AND back
    [-0.223, 0.223].forEach(pz => {
      const logoPlate = new THREE.Mesh(
        new THREE.PlaneGeometry(0.32, 0.42),
        new THREE.MeshStandardMaterial({
          color: 0xf5cf53,
          emissive: 0xd4af37,
          emissiveIntensity: 0.8,
          metalness: 0.95,
          roughness: 0.15,
          side: THREE.DoubleSide
        })
      );
      logoPlate.position.set(0, 0.05, pz);
      if (pz < 0) logoPlate.rotation.y = Math.PI;
      group.add(logoPlate);
    });

    // Two Braided Arched Handles
    function createHandle(zOffset) {
      const curve = new THREE.CubicBezierCurve3(
        new THREE.Vector3(-0.24, 0.52, zOffset),
        new THREE.Vector3(-0.24, 0.98, zOffset * 1.15),
        new THREE.Vector3(0.24, 0.98, zOffset * 1.15),
        new THREE.Vector3(0.24, 0.52, zOffset)
      );
      const tubeGeo = new THREE.TubeGeometry(curve, 28, 0.022, 8, false);
      const handleMat = new THREE.MeshStandardMaterial({
        color: 0xf5cf53,
        metalness: 0.9,
        roughness: 0.25
      });
      const handle = new THREE.Mesh(tubeGeo, handleMat);

      [-0.24, 0.24].forEach(gx => {
        const grommet = new THREE.Mesh(
          new THREE.CylinderGeometry(0.04, 0.04, 0.02, 16),
          new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.98, roughness: 0.1 })
        );
        grommet.rotation.x = Math.PI / 2;
        grommet.position.set(gx, 0.52, zOffset + (zOffset > 0 ? 0.015 : -0.015));
        group.add(grommet);
      });

      return handle;
    }

    group.add(createHandle(0.22));
    group.add(createHandle(-0.22));

    // Gold tissue paper emerging
    const tissue = new THREE.Mesh(
      new THREE.ConeGeometry(0.26, 0.32, 5),
      new THREE.MeshStandardMaterial({ color: 0xfae188, emissive: 0xd4af37, emissiveIntensity: 0.5, roughness: 0.3 })
    );
    tissue.position.set(0.06, 0.62, 0);
    tissue.rotation.z = -0.3;
    group.add(tissue);

    return group;
  }

  // 4. Cloud VPS: Enterprise 2U Rack Server Blade
  function createServerModel() {
    const group = new THREE.Group();

    // 2U Brushed Metal Server Body
    const chassis = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.42, 1.1),
      new THREE.MeshStandardMaterial({ color: 0x1e2638, metalness: 0.88, roughness: 0.22 })
    );
    group.add(chassis);

    // Rack-Mount Ears & Chrome Pull Handles
    [-0.72, 0.72].forEach((ex, idx) => {
      const ear = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.46, 0.14),
        new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.95, roughness: 0.15 })
      );
      ear.position.set(ex, 0, 0.48);
      group.add(ear);

      const handleCurve = new THREE.CubicBezierCurve3(
        new THREE.Vector3(ex, -0.15, 0.54),
        new THREE.Vector3(ex + (idx === 0 ? -0.06 : 0.06), -0.15, 0.68),
        new THREE.Vector3(ex + (idx === 0 ? -0.06 : 0.06), 0.15, 0.68),
        new THREE.Vector3(ex, 0.15, 0.54)
      );
      const handle = new THREE.Mesh(
        new THREE.TubeGeometry(handleCurve, 16, 0.02, 8, false),
        new THREE.MeshStandardMaterial({ color: 0xe2e8f0, metalness: 0.98, roughness: 0.1 })
      );
      group.add(handle);
    });

    // 8 Hot-Swap NVMe Enterprise Drive Bays
    const driveWidth = 0.13;
    const driveHeight = 0.32;
    const driveMat = new THREE.MeshStandardMaterial({ color: 0x0f172a, metalness: 0.8, roughness: 0.3 });
    const latchMat = new THREE.MeshStandardMaterial({ color: 0x475569, metalness: 0.9, roughness: 0.2 });

    for (let i = 0; i < 8; i++) {
      const dx = -0.52 + i * 0.15;
      const drive = new THREE.Mesh(new THREE.BoxGeometry(driveWidth, driveHeight, 0.04), driveMat);
      drive.position.set(dx, -0.02, 0.555);
      group.add(drive);

      const latch = new THREE.Mesh(new THREE.BoxGeometry(driveWidth * 0.85, 0.04, 0.02), latchMat);
      latch.position.set(dx, -0.14, 0.58);
      group.add(latch);

      // Status LEDs: Solid Green & Blinking Activity Blue
      const ledPower = new THREE.Mesh(
        new THREE.SphereGeometry(0.016, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0x10b981, emissive: 0x10b981, emissiveIntensity: 1.5 })
      );
      ledPower.position.set(dx - 0.03, 0.1, 0.58);
      group.add(ledPower);

      const ledAct = new THREE.Mesh(
        new THREE.SphereGeometry(0.016, 8, 8),
        new THREE.MeshStandardMaterial({ color: 0x38bdf8, emissive: 0x38bdf8, emissiveIntensity: 1.8 })
      );
      ledAct.position.set(dx + 0.03, 0.1, 0.58);
      ledAct.userData = { isLedActivity: true, seed: i };
      group.add(ledAct);
    }

    // Honeycomb Top Grill & Internal Fans
    const grill = new THREE.Mesh(
      new THREE.PlaneGeometry(1.2, 0.7),
      new THREE.MeshStandardMaterial({ color: 0x090d16, metalness: 0.9, roughness: 0.4, wireframe: true })
    );
    grill.rotation.x = -Math.PI / 2;
    grill.position.set(0, 0.215, 0);
    group.add(grill);

    for (let f = -0.4; f <= 0.4; f += 0.4) {
      const fanRing = new THREE.Mesh(
        new THREE.TorusGeometry(0.14, 0.02, 8, 24),
        new THREE.MeshStandardMaterial({ color: 0x0284c7, emissive: 0x0369a1, emissiveIntensity: 0.8 })
      );
      fanRing.rotation.x = Math.PI / 2;
      fanRing.position.set(f, 0.15, -0.1);
      group.add(fanRing);
    }

    return group;
  }

  // 5. Proxmox Virtualization: Hypervisor Blade Stack
  function createProxmoxModel() {
    const group = new THREE.Group();

    for (let l = 0; l < 3; l++) {
      const board = new THREE.Mesh(
        new THREE.BoxGeometry(1.1, 0.05, 1.1),
        new THREE.MeshStandardMaterial({ color: 0x0f172a, metalness: 0.7, roughness: 0.3 })
      );
      board.position.y = -0.3 + l * 0.3;
      group.add(board);

      const cpu = new THREE.Mesh(
        new THREE.BoxGeometry(0.35, 0.03, 0.35),
        new THREE.MeshStandardMaterial({ color: 0xf5cf53, metalness: 0.95, roughness: 0.15 })
      );
      cpu.position.set(0, board.position.y + 0.04, 0);
      group.add(cpu);
    }

    const wireCage = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(1.2, 0.85, 1.2)),
      new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.75 })
    );
    group.add(wireCage);

    for (let i = 0; i < 4; i++) {
      const vm = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.18, 0.18),
        new THREE.MeshStandardMaterial({ color: 0xf5cf53, emissive: 0xd4af37, emissiveIntensity: 0.6 })
      );
      vm.userData = { orbitOffset: i * (Math.PI / 2), orbitRadius: 0.82 };
      group.add(vm);
    }

    return group;
  }

  // 6. Golang & C++: High-Throughput Compiler Engine
  function createSystemsModel() {
    const group = new THREE.Group();

    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.55, 0),
      new THREE.MeshStandardMaterial({
        color: 0xfae188,
        emissive: 0xd4af37,
        emissiveIntensity: 0.6,
        metalness: 0.95,
        roughness: 0.12
      })
    );
    group.add(core);

    const cage = new THREE.LineSegments(
      new THREE.WireframeGeometry(new THREE.IcosahedronGeometry(0.78, 1)),
      new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.65 })
    );
    group.add(cage);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.9, 0.02, 16, 48),
      new THREE.MeshStandardMaterial({ color: 0xf5cf53, emissive: 0xd4af37, emissiveIntensity: 0.5 })
    );
    ring.rotation.x = Math.PI / 3;
    group.add(ring);

    return group;
  }

  // --------------------------------------------------------------------------
  // PRODUCT NODES DEFINITION & ORBITAL PLACEMENT
  // --------------------------------------------------------------------------
  const productData = [
    {
      id: 'tat-phone',
      category: 'fivem',
      title: 'TaT Phone',
      badge: 'FIVEM SCRIPT',
      subtitle: 'Complete Connected RP Smartphone',
      desc: 'Next-generation FiveM smartphone framework featuring 40+ included apps, Dynamic Island, Live Activities, $CashTag wallet, custom wallpapers, and camera AirDrop sharing.',
      specs: ['40+ Built-in Apps', 'Dynamic Island', '0.01 ms Footprint', 'ESX / QBCore / Qbox'],
      actionUrl: 'https://tat.tebex.io',
      actionText: 'Purchase on Tebex Store ($) ↗',
      actionUrl2: 'https://tatdocs.vercel.app/scripts/tat-phone/',
      actionText2: 'View Installation Docs ↗',
      angle: 0,
      camDist: 2.65,
      camY: 0.45,
      createMesh: createIPhoneModel
    },
    {
      id: 'tat-hud',
      category: 'fivem',
      title: 'TaT HUD Editor',
      badge: 'FIVEM SCRIPT',
      subtitle: 'In-Game Modular HUD Engine',
      desc: 'Drag-and-drop in-game HUD visual editor (F7). Includes 9 player stat layouts, 6 vehicle speedometer clusters, and MySQL per-player persistence.',
      specs: ['In-Game F7 Editor', '9 Stat Layouts', '6 Vehicle Arcs', 'MySQL Persistence'],
      actionUrl: 'https://tat.tebex.io',
      actionText: 'Get TaT HUD on Tebex ↗',
      angle: Math.PI / 3,
      camDist: 2.2,
      camY: 0.95,
      createMesh: createControllerModel
    },
    {
      id: 'cloud-vps',
      category: 'cloud',
      title: 'Monolith Cloud VPS',
      badge: 'MONOLITH.HOST',
      subtitle: 'AMD Ryzen 9 7950X High-Frequency',
      desc: 'Ultra-low latency KVM virtual servers powered by AMD Ryzen 9 7950X processors, PCIe Gen4 NVMe in RAID 10, and multi-terabit Corero DDoS mitigation.',
      specs: ['5.7 GHz Ryzen 9', 'NVMe RAID 10', 'Tier-4 Global Nodes', 'Instant KVM Setup'],
      actionUrl: 'https://monolith.host',
      actionText: 'Deploy on Monolith.host ↗',
      angle: 2 * Math.PI / 3,
      camDist: 3.25,
      camY: 0.65,
      createMesh: createServerModel
    },
    {
      id: 'proxmox',
      category: 'engineering',
      title: 'Proxmox Virtualization',
      badge: 'MNLTH ENGINEERING',
      subtitle: 'Hypervisor Clusters & Custom Web Panels',
      desc: 'Custom Proxmox VE hypervisor installations, turnkey API integration with your customer dashboard, automated VM provisioning, and live HA failover.',
      specs: ['Cluster HA Setup', 'Custom Web Panels', 'Bespoke API Integrations', 'Ceph Storage'],
      actionUrl: '#',
      actionModal: 'contact',
      actionText: 'Request Proxmox Consultation ↗',
      angle: Math.PI,
      camDist: 2.85,
      camY: 0.55,
      createMesh: createProxmoxModel
    },
    {
      id: 'ecommerce',
      category: 'engineering',
      title: 'Shopify & WooCommerce',
      badge: 'MNLTH ENGINEERING',
      subtitle: 'Bespoke High-Conversion E-Commerce',
      desc: 'Custom Shopify Liquid development, headless WooCommerce stores, automated Stripe/Crypto merchant API gateways, and frictionless shopping carts.',
      specs: ['Shopify Liquid', 'WooCommerce Headless', 'Stripe & Crypto Merchant', 'Conversion Tuning'],
      actionUrl: '#',
      actionModal: 'contact',
      actionText: 'Build Your Store With Us ↗',
      angle: 4 * Math.PI / 3,
      camDist: 3.15,
      camY: 0.55,
      createMesh: createShoppingBagModel
    },
    {
      id: 'systems',
      category: 'engineering',
      title: 'Golang & C++ Applications',
      badge: 'MNLTH ENGINEERING',
      subtitle: 'Low-Latency High-Concurrency Systems',
      desc: 'High-throughput Golang backend microservices, real-time WebSocket sync servers, and native C++ game engine modules engineered for extreme speed and stability.',
      specs: ['Golang Goroutines', 'C++ Native Core', 'Zero Memory Leaks', 'Sub-Millisecond Latency'],
      actionUrl: '#',
      actionModal: 'contact',
      actionText: 'Discuss Systems Engineering ↗',
      angle: 5 * Math.PI / 3,
      camDist: 2.75,
      camY: 0.5,
      createMesh: createSystemsModel
    }
  ];

  const nodeMeshes = [];
  const nodeLabels = [];
  const radius = 4.0;

  productData.forEach((item) => {
    const nodeGroup = new THREE.Group();
    nodeGroup.userData = item;

    const mesh = item.createMesh();
    nodeGroup.add(mesh);

    // Soft Contact Shadow Disc under every model for grounding
    const shadowDisc = new THREE.Mesh(
      new THREE.RingGeometry(0.05, 0.8, 32),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.5, side: THREE.DoubleSide })
    );
    shadowDisc.rotation.x = Math.PI / 2;
    shadowDisc.position.y = -0.95;
    nodeGroup.add(shadowDisc);

    // Generous Invisible Hit Sphere for reliable clicking & touch
    const hitBox = new THREE.Mesh(
      new THREE.SphereGeometry(1.0, 12, 12),
      new THREE.MeshBasicMaterial({ visible: false })
    );
    nodeGroup.add(hitBox);

    // Position along orbit
    const x = Math.cos(item.angle) * radius;
    const z = Math.sin(item.angle) * radius;
    nodeGroup.position.set(x, 0, z);

    // Orient node so its front face (+z) faces outward into the orbit at a pleasing 3/4 perspective
    nodeGroup.rotation.y = Math.atan2(x, z) + 0.28;

    // Dynamic Connection Beam to center
    const lineGeo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(x * 0.75, 0, z * 0.75)
    ]);
    const lineMat = new THREE.LineBasicMaterial({
      color: 0xd4af37,
      transparent: true,
      opacity: 0.25
    });
    const beam = new THREE.Line(lineGeo, lineMat);
    scene.add(beam);

    scene.add(nodeGroup);
    nodeMeshes.push(nodeGroup);

    // 3D HTML Floating Label
    const label = document.createElement('div');
    label.className = 'holodeck-node-label';
    label.textContent = item.title;
    label.title = `Click to inspect ${item.title}`;
    label.addEventListener('click', (e) => {
      e.stopPropagation();
      focusOnNode(nodeGroup);
    });
    label.addEventListener('mouseenter', () => {
      sound.playHoverTone();
    });
    container.appendChild(label);
    nodeLabels.push(label);
  });

  // Quick Navigation Jumper Pills below canvas
  const jumperContainer = document.getElementById('holodeckJumpers');
  if (jumperContainer) {
    jumperContainer.innerHTML = '';
    productData.forEach((item, idx) => {
      const btn = document.createElement('button');
      btn.className = 'holodeck-jumper-btn';
      btn.textContent = item.title;
      btn.addEventListener('click', () => {
        focusOnNode(nodeMeshes[idx]);
      });
      jumperContainer.appendChild(btn);
    });
  }

  // --------------------------------------------------------------------------
  // INTERACTIVE CONTROLS: 360° ORBIT & DAMPING PHYSICS
  // --------------------------------------------------------------------------
  let isDragging = false;
  let prevMouseX = 0;
  let prevMouseY = 0;
  let orbitAngleY = 0;
  let orbitAngleX = 0.35;
  let orbitVelocityY = 0.002;
  let orbitDistance = 9.2;
  let isFocusedOnNode = false;
  let focusedNode = null;

  const canvas = renderer.domElement;

  canvas.addEventListener('mousedown', (e) => {
    isDragging = true;
    prevMouseX = e.clientX;
    prevMouseY = e.clientY;
  });

  window.addEventListener('mouseup', () => {
    isDragging = false;
  });

  window.addEventListener('mousemove', (e) => {
    if (!isDragging || isFocusedOnNode) return;
    const dx = e.clientX - prevMouseX;
    const dy = e.clientY - prevMouseY;
    prevMouseX = e.clientX;
    prevMouseY = e.clientY;

    orbitAngleY -= dx * 0.0055;
    orbitAngleX = Math.max(-0.2, Math.min(1.1, orbitAngleX + dy * 0.0055));
    orbitVelocityY = 0;
  });

  canvas.addEventListener('touchstart', (e) => {
    if (e.touches.length === 1) {
      isDragging = true;
      prevMouseX = e.touches[0].clientX;
      prevMouseY = e.touches[0].clientY;
    }
  }, { passive: true });

  window.addEventListener('touchend', () => {
    isDragging = false;
  });

  window.addEventListener('touchmove', (e) => {
    if (!isDragging || isFocusedOnNode || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - prevMouseX;
    const dy = e.touches[0].clientY - prevMouseY;
    prevMouseX = e.touches[0].clientX;
    prevMouseY = e.touches[0].clientY;

    orbitAngleY -= dx * 0.0075;
    orbitAngleX = Math.max(-0.2, Math.min(1.1, orbitAngleX + dy * 0.0075));
    orbitVelocityY = 0;
  }, { passive: true });

  canvas.addEventListener('wheel', (e) => {
    if (isFocusedOnNode) return;
    e.preventDefault();
    orbitDistance = Math.max(5.5, Math.min(13.5, orbitDistance + e.deltaY * 0.008));
  }, { passive: false });

  // --------------------------------------------------------------------------
  // RAYCASTING & NODE INSPECTION
  // --------------------------------------------------------------------------
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2(-999, -999);
  let hoveredNode = null;

  function updateMouseCoord(e) {
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  canvas.addEventListener('mousemove', (e) => {
    updateMouseCoord(e);
  });

  canvas.addEventListener('click', (e) => {
    updateMouseCoord(e);
    raycaster.setFromCamera(mouse, camera);

    const hit = findIntersectedNode(raycaster);
    if (hit) {
      focusOnNode(hit);
    }
  });

  function findIntersectedNode(rc) {
    for (const nodeGroup of nodeMeshes) {
      const intersects = rc.intersectObjects(nodeGroup.children, true);
      if (intersects.length > 0) {
        return nodeGroup;
      }
    }
    return null;
  }

  // Gamified Discovery Tracker State
  const discoveredSet = new Set();
  const trackerFill = document.getElementById('holodeckTrackerFill');
  const trackerCount = document.getElementById('holodeckTrackerCount');
  const trackerBadge = document.getElementById('holodeckBadgeUnlocked');

  function markNodeDiscovered(id) {
    if (!discoveredSet.has(id)) {
      discoveredSet.add(id);
      const total = productData.length;
      const progress = discoveredSet.size;

      if (trackerCount) {
        trackerCount.textContent = `${progress} / ${total}`;
      }
      if (trackerFill) {
        trackerFill.style.width = `${(progress / total) * 100}%`;
      }

      if (progress === total && trackerBadge) {
        trackerBadge.style.display = 'inline-block';
        sound.playDiscoveryFanfare();
      }
    }
  }

  // Holo-Inspector Presentation
  const inspector = document.getElementById('holoInspector');
  const inspectorBadge = document.getElementById('holoInspectorBadge');
  const inspectorTitle = document.getElementById('holoInspectorTitle');
  const inspectorDesc = document.getElementById('holoInspectorDesc');
  const inspectorSpecs = document.getElementById('holoInspectorSpecs');
  const inspectorActions = document.getElementById('holoInspectorActions');
  const inspectorClose = document.getElementById('holoInspectorClose');

  function openInspector(data) {
    if (!inspector) return;

    if (inspectorBadge) inspectorBadge.textContent = data.badge;
    if (inspectorTitle) inspectorTitle.textContent = data.title;
    if (inspectorDesc) inspectorDesc.textContent = data.desc;

    if (inspectorSpecs) {
      inspectorSpecs.innerHTML = '';
      data.specs.forEach(s => {
        const span = document.createElement('span');
        span.className = 'tech-tag';
        span.textContent = s;
        inspectorSpecs.appendChild(span);
      });
    }

    if (inspectorActions) {
      inspectorActions.innerHTML = '';
      const primaryBtn = document.createElement('a');
      primaryBtn.href = data.actionUrl;
      primaryBtn.className = 'btn btn-primary';
      primaryBtn.textContent = data.actionText;
      if (data.actionModal) {
        primaryBtn.setAttribute('data-open-modal', data.actionModal);
      } else {
        primaryBtn.target = '_blank';
        primaryBtn.rel = 'noopener noreferrer';
      }
      primaryBtn.addEventListener('click', () => sound.playClickPulse());
      inspectorActions.appendChild(primaryBtn);

      if (data.actionUrl2) {
        const secBtn = document.createElement('a');
        secBtn.href = data.actionUrl2;
        secBtn.className = 'btn btn-secondary';
        secBtn.textContent = data.actionText2;
        secBtn.target = '_blank';
        secBtn.rel = 'noopener noreferrer';
        secBtn.addEventListener('click', () => sound.playClickPulse());
        inspectorActions.appendChild(secBtn);
      }
    }

    inspector.classList.add('is-active');
  }

  function closeInspector() {
    if (inspector) {
      inspector.classList.remove('is-active');
    }
    resetCamera();
  }

  if (inspectorClose) {
    inspectorClose.addEventListener('click', () => {
      sound.playClickPulse();
      closeInspector();
    });
  }

  function focusOnNode(nodeGroup) {
    const data = nodeGroup.userData;
    isFocusedOnNode = true;
    focusedNode = nodeGroup;

    sound.playClickPulse();
    sound.playWarpSound();
    markNodeDiscovered(data.id);

    const nodePos = nodeGroup.position;
    targetLookAt.copy(nodePos);

    // Frame front face of model from outside the orbit ring looking inward at the hero 3/4 angle
    const outward = new THREE.Vector3(nodePos.x, 0, nodePos.z).normalize();
    const dist = data.camDist || 2.8;
    const camY = data.camY || 0.55;
    targetCamPos.set(
      nodePos.x + outward.x * dist,
      nodePos.y + camY,
      nodePos.z + outward.z * dist
    );

    openInspector(data);
  }

  function resetCamera() {
    isFocusedOnNode = false;
    focusedNode = null;
    targetLookAt.copy(defaultTarget);
    targetCamPos.copy(defaultCamPos);
  }

  const resetBtn = document.getElementById('holodeckResetBtn');
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      sound.playClickPulse();
      closeInspector();
    });
  }

  // Audio Toggle Button in HUD
  const audioBtn = document.getElementById('holodeckAudioBtn');
  if (audioBtn) {
    const updateAudioLabel = () => {
      const muted = sound.isMuted();
      audioBtn.innerHTML = muted 
        ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="1" y1="1" x2="23" y2="23"></line><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path></svg> Audio: OFF`
        : `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg> Audio: ON`;
    };
    updateAudioLabel();

    audioBtn.addEventListener('click', () => {
      sound.toggleMute();
      sound.playClickPulse();
      updateAudioLabel();
    });
  }

  // Category Filtering
  const filterBtns = document.querySelectorAll('.holodeck-filter-btn');
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      sound.playClickPulse();
      filterBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');

      const cat = btn.getAttribute('data-category');
      nodeMeshes.forEach(node => {
        const itemCat = node.userData.category;
        const matches = (cat === 'all' || itemCat === cat);

        node.visible = true;
        const targetScale = matches ? 1.0 : 0.45;
        node.scale.set(targetScale, targetScale, targetScale);
      });
    });
  });

  // Resize Handler
  function onResize() {
    if (!container) return;
    const newW = container.clientWidth || 1000;
    const newH = container.clientHeight || 600;
    camera.aspect = newW / newH;
    camera.updateProjectionMatrix();
    renderer.setSize(newW, newH);
  }
  window.addEventListener('resize', onResize);

  // --------------------------------------------------------------------------
  // ANIMATION & RENDER LOOP
  // --------------------------------------------------------------------------
  const startMs = performance.now();

  function animate() {
    requestAnimationFrame(animate);
    const time = (performance.now() - startMs) * 0.001;

    // Orbit camera calculation when free
    if (!isFocusedOnNode) {
      orbitAngleY += orbitVelocityY;
      const cx = Math.sin(orbitAngleY) * Math.cos(orbitAngleX) * orbitDistance;
      const cy = Math.sin(orbitAngleX) * orbitDistance;
      const cz = Math.cos(orbitAngleY) * Math.cos(orbitAngleX) * orbitDistance;
      targetCamPos.set(cx, cy, cz);
    }

    // Smooth camera interpolation
    camera.position.lerp(targetCamPos, 0.075);
    currentCamTarget.lerp(targetLookAt, 0.075);
    camera.lookAt(currentCamTarget);

    // Rotate Central Monolith Portal
    centerMonolithGroup.rotation.y = time * 0.35;
    centerMonolithGroup.position.y = Math.sin(time * 1.5) * 0.06;

    // Node Animations
    nodeMeshes.forEach((node, idx) => {
      node.position.y = Math.sin(time * 2 + idx) * 0.12;

      // Only slow-spin when not actively inspected
      if (node !== focusedNode) {
        node.rotation.y += 0.005;
      }

      // Animate orbiting sub-elements (like apps or VM cubes)
      node.children[0]?.children.forEach(child => {
        if (child.userData?.orbitRadius) {
          const cAngle = time * 2.5 + child.userData.orbitOffset;
          child.position.x = Math.cos(cAngle) * child.userData.orbitRadius;
          child.position.z = Math.sin(cAngle) * child.userData.orbitRadius;
        }
        // Blinking server drive activity LEDs
        if (child.userData?.isLedActivity) {
          const flash = Math.sin(time * 14 + child.userData.seed * 3) > 0.25;
          child.material.emissiveIntensity = flash ? 2.5 : 0.2;
        }
      });
    });

    // Project 3D labels to screen coordinates
    const projPos = new THREE.Vector3();
    const curW = container.clientWidth || width;
    const curH = container.clientHeight || height;

    nodeMeshes.forEach((node, idx) => {
      const lbl = nodeLabels[idx];
      if (!lbl) return;

      if (!node.visible) {
        lbl.style.display = 'none';
        return;
      }

      node.getWorldPosition(projPos);
      projPos.project(camera);

      if (projPos.z > 1) {
        lbl.style.display = 'none';
      } else {
        lbl.style.display = 'flex';
        const scrX = (projPos.x * 0.5 + 0.5) * curW;
        const scrY = (-(projPos.y * 0.5) + 0.5) * curH - 34;
        lbl.style.left = `${scrX}px`;
        lbl.style.top = `${scrY}px`;
      }
    });

    // Starfield subtle rotation
    starField.rotation.y = time * 0.02;

    // Hover Raycasting check
    raycaster.setFromCamera(mouse, camera);
    const hitNode = findIntersectedNode(raycaster);

    if (hitNode !== hoveredNode) {
      if (hoveredNode) {
        hoveredNode.scale.set(1, 1, 1);
      }
      hoveredNode = hitNode;
      if (hoveredNode) {
        hoveredNode.scale.set(1.15, 1.15, 1.15);
        sound.playHoverTone();
      }
    }

    renderer.render(scene, camera);
  }

  animate();
}
